/**
 * Guest Identity Service
 * Centralise toute la logique d'identification des utilisateurs invités.
 *
 * Deux identifiants complémentaires :
 * - deviceId  : identifiant local stable (localStorage), utilisé comme creator_id
 * - fingerprint : empreinte navigateur (async), utilisé pour les requêtes Supabase (guest_fingerprint)
 *
 * Ce service est la source unique de vérité pour l'identité guest.
 * Les autres modules (pollStorage, dashboard, PollService) doivent l'utiliser
 * au lieu de recalculer ces valeurs indépendamment.
 */

import { logger } from "./logger";
import { isE2ETestingEnvironment } from "./e2e-detection";

// ============================================================================
// CONSTANTS
// ============================================================================

const DEVICE_ID_STORAGE_KEY = "doodates_device_id";

// ============================================================================
// CACHE (module-level pour stabilité dans les tests)
// ============================================================================

let deviceIdCache: string | null = null;
let fingerprintCache: string | null = null;

// ============================================================================
// DEVICE ID — synchrone, stable, localStorage
// ============================================================================

function hasWindow(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

/**
 * Retourne le deviceId stable de cet appareil.
 * Génère et persiste un nouvel ID si aucun n'existe.
 */
export function getDeviceId(): string {
  if (deviceIdCache) return deviceIdCache;
  if (!hasWindow()) return (deviceIdCache = "server");

  const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing && existing.trim()) {
    // Valider que l'UUID existant est valide (format standard UUID v4)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(existing)) {
      deviceIdCache = existing;
      return existing;
    } else if (existing.startsWith("dev-")) {
      // Migration: retirer le préfixe dev- des anciens UUIDs
      const cleanUuid = existing.substring(4);
      if (uuidRegex.test(cleanUuid)) {
        window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, cleanUuid);
        deviceIdCache = cleanUuid;
        console.log("✅ Migrated dev- prefixed UUID to standard UUID");
        return cleanUuid;
      }
    }
    // UUID invalide, en générer un nouveau
    console.warn(
      "⚠️ UUID invalide détecté dans localStorage, génération d'un nouvel UUID",
    );
    window.localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
  }

  // Générer un deviceId UUID valide (sans préfixe pour compatibilité PostgreSQL)
  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    },
  );
  window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, uuid);
  deviceIdCache = uuid;
  return uuid;
}

// ============================================================================
// FINGERPRINT — async, basé sur le navigateur
// ============================================================================

/**
 * Retourne le fingerprint navigateur (async).
 * Utilise le cache mémoire si disponible, sinon délègue à browserFingerprint.
 */
export async function getFingerprint(): Promise<string> {
  if (fingerprintCache) return fingerprintCache;

  try {
    const { getCachedFingerprint } = await import("./browserFingerprint");
    const raw = await getCachedFingerprint();
    // Filet de sécurité côté source (une seule fois, pour tous les appelants) :
    // un navigateur piloté par Playwright/Selenium/etc. est détecté sans
    // dépendre d'une convention de nommage par test — navigator.webdriver
    // est positionné automatiquement, contrairement aux préfixes ad hoc
    // ("e2e-fp-", "enf-...-fp-"...) qu'un test peut oublier de respecter.
    // Le trigger SQL tag_test_fingerprint_from_poll (et son équivalent sur
    // guest_quotas) reconnaît ce préfixe et exclut automatiquement ces
    // fingerprints des stats admin, y compris pour les tests qui consomment
    // du quota IA sans jamais créer de sondage.
    fingerprintCache =
      isE2ETestingEnvironment() && !raw.startsWith("e2e-") ? `e2e-${raw}` : raw;
    return fingerprintCache;
  } catch (error) {
    logger.warn(
      "⚠️ Impossible de récupérer le fingerprint, fallback deviceId",
      "auth",
    );
    return getDeviceId();
  }
}

// ============================================================================
// IDENTITÉ COMPLÈTE
// ============================================================================

export interface GuestIdentity {
  deviceId: string;
  fingerprint: string;
  stableFingerprint: string;
}

/**
 * Retourne le fingerprint "stable" (clé secondaire cross-browser).
 */
export async function getStableFingerprint(): Promise<string> {
  try {
    const { getCachedStableFingerprint } = await import("./browserFingerprint");
    return await getCachedStableFingerprint();
  } catch (error) {
    logger.warn("Stable fingerprint unavailable", "auth", { error });
    return "";
  }
}

/**
 * Retourne l'identité complète de l'invité (deviceId + fingerprint + stable).
 */
export async function getGuestIdentity(): Promise<GuestIdentity> {
  const deviceId = getDeviceId();
  const [fingerprint, stableFingerprint] = await Promise.all([
    getFingerprint(),
    getStableFingerprint(),
  ]);
  return { deviceId, fingerprint, stableFingerprint };
}

// ============================================================================
// JETON DE POSSESSION DES SONDAGES INVITÉS (#828)
// ============================================================================

const GUEST_OWNER_TOKEN_KEY = "__dd_guest_owner_token";

let guestOwnerTokenCache: string | null = null;

/**
 * Retourne le jeton de possession des sondages invités de ce navigateur
 * (généré à la demande, persisté sans expiration).
 *
 * Sécurité (#828) : la propriété des sondages invités reposait sur le
 * fingerprint navigateur — dérivable par n'importe quel site, donc
 * usurpable (IDOR sur delete/set_poll_status/claim). Ce jeton aléatoire
 * 256 bits est inforgeable ; le serveur n'en stocke que le hash SHA-256
 * (polls.guest_owner_token_hash). Ne JAMAIS l'exposer hors des appels RPC
 * de propriété. Retourne null hors navigateur (SSR) ou sans WebCrypto.
 */
export function getGuestOwnerToken(): string | null {
  if (guestOwnerTokenCache) return guestOwnerTokenCache;
  if (!hasWindow()) return null;

  try {
    const existing = window.localStorage.getItem(GUEST_OWNER_TOKEN_KEY);
    // Longueur minimale alignée sur la validation serveur (>= 32 chars).
    if (existing && existing.trim().length >= 32) {
      guestOwnerTokenCache = existing;
      return existing;
    }

    const cryptoObj = globalThis.crypto;
    if (!cryptoObj || typeof cryptoObj.getRandomValues !== "function") {
      return null;
    }
    const bytes = cryptoObj.getRandomValues(new Uint8Array(32));
    const token = Array.from(bytes, (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
    window.localStorage.setItem(GUEST_OWNER_TOKEN_KEY, token);
    guestOwnerTokenCache = token;
    return token;
  } catch (error) {
    logger.warn("Guest owner token unavailable", "auth", { error });
    return null;
  }
}

// ============================================================================
// TROUSSEAU DE JETONS D'ADMIN (défaut D3, spec « utilisable par les IA » §5.5)
// ============================================================================
//
// Avant : un lien d'admin entrant (`?owner_token=`) REMPLAÇAIT le jeton
// principal du navigateur (`setGuestOwnerToken`, supprimé) — ouvrir le lien
// d'un sondage faisait perdre l'accès à tous les autres sondages invités de ce
// navigateur. Désormais, un jeton reçu par lien va dans un trousseau
// `pollId → jeton`, et le jeton principal n'est plus jamais écrasé.
//
// Clés du trousseau : l'identifiant du sondage (UUID) de préférence. Un slug
// est accepté aussi, pour pouvoir installer le jeton dès l'arrivée sur la page
// (seul le slug est connu avant le chargement du sondage) ; la page l'ajoute
// ensuite sous l'UUID. Les deux entrées portent le même jeton et partent
// ensemble (`removeKeyringTokens` retire par jeton).

const GUEST_OWNER_TOKENS_KEY = "__dd_guest_owner_tokens";
const MIN_OWNER_TOKEN_LENGTH = 32;
/** Plafond défensif : le trousseau n'a pas vocation à grossir sans fin. */
const MAX_KEYRING_ENTRIES = 200;

function isValidOwnerToken(token: unknown): token is string {
  return (
    typeof token === "string" && token.trim().length >= MIN_OWNER_TOKEN_LENGTH
  );
}

/** Lecture tolérante : stockage absent, bloqué ou JSON corrompu → {}. */
function readKeyring(): Record<string, string> {
  if (!hasWindow()) return {};
  try {
    const raw = window.localStorage.getItem(GUEST_OWNER_TOKENS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      if (key && isValidOwnerToken(value)) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function writeKeyring(keyring: Record<string, string>): boolean {
  if (!hasWindow()) return false;
  try {
    if (Object.keys(keyring).length === 0) {
      window.localStorage.removeItem(GUEST_OWNER_TOKENS_KEY);
    } else {
      window.localStorage.setItem(
        GUEST_OWNER_TOKENS_KEY,
        JSON.stringify(keyring),
      );
    }
    return true;
  } catch (error) {
    logger.warn("Guest owner keyring unavailable", "auth", { error });
    return false;
  }
}

/**
 * Range le jeton d'admin d'UN sondage dans le trousseau (lien d'admin reçu
 * par e-mail ou produit par une IA). Ne touche jamais au jeton principal.
 * Jeton trop court (< 32) ignoré : il ne pourrait matcher aucun hash serveur.
 * Ne lève jamais ; renvoie false si rien n'a été enregistré.
 */
export function addPollOwnerToken(
  pollIdOrSlug: string,
  token: string,
): boolean {
  if (!pollIdOrSlug || !isValidOwnerToken(token)) return false;
  const keyring = readKeyring();
  keyring[pollIdOrSlug] = token.trim();
  const keys = Object.keys(keyring);
  if (keys.length > MAX_KEYRING_ENTRIES) {
    // Ordre d'insertion des clés non numériques : les plus anciennes d'abord.
    for (const key of keys.slice(0, keys.length - MAX_KEYRING_ENTRIES)) {
      delete keyring[key];
    }
  }
  return writeKeyring(keyring);
}

/**
 * Jeton à présenter pour prouver la propriété d'UN sondage précis : celui du
 * trousseau s'il existe (par UUID, puis par slug), sinon le jeton principal
 * du navigateur. À utiliser pour tout appel de propriété lié à un sondage ;
 * les CRÉATIONS gardent `getGuestOwnerToken()` (le jeton principal).
 */
export function getOwnerTokenForPoll(
  pollId?: string | null,
  slug?: string | null,
): string | null {
  const keyring = readKeyring();
  if (pollId && keyring[pollId]) return keyring[pollId];
  if (slug && keyring[slug]) return keyring[slug];
  return getGuestOwnerToken();
}

/**
 * Jeton du TROUSSEAU pour un sondage (par UUID, puis par slug), sans repli
 * sur le jeton principal. Non nul seulement si un lien d'admin de ce sondage
 * a été ouvert — et vérifié par le serveur — dans ce navigateur.
 */
export function getKeyringTokenForPoll(
  pollId?: string | null,
  slug?: string | null,
): string | null {
  const keyring = readKeyring();
  if (pollId && keyring[pollId]) return keyring[pollId];
  if (slug && keyring[slug]) return keyring[slug];
  return null;
}

/** Entrées du trousseau (sans le jeton principal). */
export function listKeyringTokens(): Array<{ key: string; token: string }> {
  return Object.entries(readKeyring()).map(([key, token]) => ({ key, token }));
}

/**
 * Retire du trousseau toutes les entrées portant l'un de ces jetons (après
 * rattachement à un compte : le sondage n'est plus « invité », le jeton ne
 * prouve plus rien).
 */
export function removeKeyringTokens(tokens: string[]): void {
  if (tokens.length === 0) return;
  const drop = new Set(tokens);
  const keyring = readKeyring();
  let changed = false;
  for (const [key, token] of Object.entries(keyring)) {
    if (drop.has(token)) {
      delete keyring[key];
      changed = true;
    }
  }
  if (changed) writeKeyring(keyring);
}

/**
 * Retire du trousseau ces clés précises (UUID et slug d'UN sondage), sans
 * toucher aux autres sondages qui porteraient le même jeton.
 */
export function removeKeyringKeys(keys: string[]): void {
  if (keys.length === 0) return;
  const keyring = readKeyring();
  let changed = false;
  for (const key of keys) {
    if (key && key in keyring) {
      delete keyring[key];
      changed = true;
    }
  }
  if (changed) writeKeyring(keyring);
}

// ============================================================================
// LIEN D'ADMIN : FRAGMENT `#owner_token=` (défaut D4)
// ============================================================================

const OWNER_TOKEN_PARAM = "owner_token";

/**
 * Lit le jeton d'admin d'une URL : le fragment d'abord (`#owner_token=`,
 * nouveaux liens — jamais envoyé au serveur), puis la query
 * (`?owner_token=`, liens déjà envoyés par e-mail). Renvoie aussi l'URL
 * nettoyée (chemin + query + fragment restants) à passer à
 * `history.replaceState`, ou null si l'URL ne portait aucun jeton.
 */
export function extractOwnerTokenFromUrl(href: string): {
  token: string | null;
  cleanUrl: string | null;
} {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { token: null, cleanUrl: null };
  }

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const fromHash = hashParams.get(OWNER_TOKEN_PARAM);
  const fromQuery = url.searchParams.get(OWNER_TOKEN_PARAM);
  if (fromHash === null && fromQuery === null) {
    return { token: null, cleanUrl: null };
  }

  hashParams.delete(OWNER_TOKEN_PARAM);
  url.searchParams.delete(OWNER_TOKEN_PARAM);
  const restHash = hashParams.toString();
  const cleanUrl = url.pathname + url.search + (restHash ? `#${restHash}` : "");

  const candidate = fromHash || fromQuery;
  return {
    token: isValidOwnerToken(candidate) ? candidate.trim() : null,
    cleanUrl,
  };
}

/** Construit le lien d'admin d'un sondage (jeton dans le fragment). */
export function buildOwnerAdminUrl(
  origin: string,
  slug: string,
  token: string,
): string {
  return `${origin}/poll/${encodeURIComponent(slug)}/results#${OWNER_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}

// ============================================================================
// CURRENT USER ID — unifié auth + guest
// ============================================================================

/**
 * Retourne l'identifiant de l'utilisateur courant.
 * - Si authentifié : retourne l'ID Supabase
 * - Si invité : retourne le deviceId
 */
export function getCurrentUserId(authenticatedUserId?: string | null): string {
  if (authenticatedUserId) {
    return authenticatedUserId;
  }
  return getDeviceId();
}

// ============================================================================
// HELPERS POUR LES POLLS
// ============================================================================

/**
 * Enrichit les données d'un poll avec les identifiants guest.
 * Pour les invités : creator_id = null (colonne UUID, pas de deviceId),
 * identification via guest_fingerprint uniquement.
 * Pour les connectés : creator_id inchangé (UUID Supabase).
 */
export async function enrichPollForGuest(poll: {
  creator_id?: string | null;
  guest_fingerprint?: string | null;
}): Promise<{ creator_id: string | null; guest_fingerprint: string }> {
  const { fingerprint } = await getGuestIdentity();

  // Si creator_id est un UUID valide (utilisateur connecté), le garder
  // Sinon (guest, anonymous, deviceId, undefined) → null
  const isValidUuid =
    poll.creator_id &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      poll.creator_id,
    );
  const creatorId = isValidUuid ? poll.creator_id! : null;

  return {
    creator_id: creatorId,
    guest_fingerprint: fingerprint,
  };
}

// ============================================================================
// HELPERS POUR LES CONVERSATIONS
// ============================================================================

/**
 * Enrichit les données d'une conversation avec les identifiants guest.
 * Pour les invités : user_id = null, identification via guest_fingerprint.
 * Pour les connectés : user_id inchangé (UUID Supabase).
 */
export async function enrichConversationForGuest(conv: {
  user_id?: string | null;
  guest_fingerprint?: string | null;
}): Promise<{
  user_id: string | null;
  guest_fingerprint: string;
  creator_type: "guest";
}> {
  const { fingerprint } = await getGuestIdentity();

  const isValidUuid =
    conv.user_id &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      conv.user_id,
    );
  const userId = isValidUuid ? conv.user_id! : null;

  return {
    user_id: userId,
    guest_fingerprint: fingerprint,
    creator_type: "guest",
  };
}

// ============================================================================
// RÉCUPÉRATION DES POLLS GUEST
// ============================================================================

/**
 * Récupère les polls de l'invité depuis Supabase.
 * Identification uniquement par guest_fingerprint (creator_id est null pour les invités).
 */
export async function getGuestPolls(): Promise<any[]> {
  try {
    const { pollService } = await import("../services/PollService");
    const fingerprint = await getFingerprint();

    logger.info("🔍 GuestIdentity - Recherche polls par fingerprint", "auth", {
      fingerprint: fingerprint.substring(0, 16),
    });

    const polls = await pollService.getPollsByGuest(fingerprint);

    logger.info("🔍 GuestIdentity - Résultat", "auth", {
      count: polls.length,
    });

    return polls;
  } catch (error) {
    logger.warn(
      "⚠️ GuestIdentity - Erreur récupération polls Supabase",
      "auth",
      { error },
    );
    return [];
  }
}

// ============================================================================
// TEST HELPERS
// ============================================================================

/**
 * Réinitialise les caches (pour les tests uniquement).
 */
export function _resetCacheForTests(): void {
  deviceIdCache = null;
  fingerprintCache = null;
  guestOwnerTokenCache = null;
}

// ============================================================================
// TEST MULTI-IDENTITÉ (preview/local uniquement)
// ============================================================================

// Domaines de prod — jamais autorisés, quelle que soit la façon dont cette
// fonction est appelée (défense en profondeur : le bouton UI qui l'appelle
// est déjà cliqué derrière isTestIdentitySwitchAllowed(), mais un attaquant
// qui invoquerait la fonction directement depuis la console ne doit rien
// obtenir de plus qu'un utilisateur normal — elle n'ouvre aucun accès, elle
// efface juste l'identité locale de CE navigateur).
const PROD_HOSTNAMES = new Set([
  "doodates.com",
  "www.doodates.com",
  "dooquest.com",
  "www.dooquest.com",
]);

/**
 * True hors des domaines de prod (preview Vercel `*.vercel.app`, localhost,
 * tout autre hostname). Sert à afficher/masquer le bouton de test
 * multi-identité — jamais un contrôle de sécurité côté serveur, juste un
 * confort de test qui ne doit pas apparaître pour de vrais utilisateurs.
 */
export function isTestIdentitySwitchAllowed(): boolean {
  if (!hasWindow()) return false;
  return !PROD_HOSTNAMES.has(window.location.hostname);
}

/**
 * Efface l'identité invité de CE navigateur (fingerprint, device id, jeton de
 * possession) pour permettre de tester plusieurs "personnes" dans le même
 * navigateur sans profils séparés — utile en preview Vercel où Cowork/CI
 * n'a pas forcément plusieurs profils navigateur disponibles.
 *
 * No-op sur les domaines de prod (cf. PROD_HOSTNAMES) : même appelée
 * directement, elle ne fait rien — pas de contournement possible depuis la
 * console d'un vrai visiteur.
 *
 * N'efface RIEN côté serveur : aucun sondage, vote ou compte n'est touché.
 * Seul l'effet est que ce navigateur n'est plus reconnu comme l'auteur de
 * ses votes/sondages précédents tant qu'il n'a pas regénéré une identité
 * (automatique au prochain appel de getFingerprint()/getGuestOwnerToken()).
 */
export function resetGuestIdentityForTesting(): void {
  if (!isTestIdentitySwitchAllowed()) return;
  if (!hasWindow()) return;

  window.localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
  window.localStorage.removeItem(GUEST_OWNER_TOKEN_KEY);
  window.localStorage.removeItem(GUEST_OWNER_TOKENS_KEY);
  // Clés de packages/core/src/lib/browserFingerprint.ts — pas d'export
  // partagé pour ces constantes, dupliquées ici intentionnellement (module
  // séparé, couplage volontairement lâche).
  window.localStorage.removeItem("__dd_fingerprint");
  window.localStorage.removeItem("__dd_stable_fingerprint");

  _resetCacheForTests();
}
